import { useCallback, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import ProgressBar from "react-bootstrap/ProgressBar";
import Spinner from "react-bootstrap/Spinner";
import Nav from "react-bootstrap/Nav";
import ListGroup from "react-bootstrap/ListGroup";
import Form from "react-bootstrap/Form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBroom,
    faCheckCircle,
    faCircleExclamation,
    faExclamationTriangle,
    faHistory,
    faHourglassHalf,
    faRotateLeft,
    faSync,
    faTrash,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import { getErrorMessage } from "../helpers/errorHelper";
import { confirmAction } from "../helpers/alertHelper";
import { formatRelativeTime } from "../helpers/formatHelper";
import { CleanupRunViewModel, DeletedVideosViewModel, PendingDeletionViewModel, VideoViewModel } from "../apiClient";
import { uncategorizedText } from "../constants";

type CleanupTab = "pending" | "deleted";

const formatDeletedDate = (date?: Date | null) => {
    if (!date) return null;

    return new Date(date).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

const FailureDetails = ({ video }: { video: VideoViewModel }) => {
    if (!video.lastFailureReason && !video.lastFailureHtml) return null;

    const relative = formatRelativeTime(video.lastFailureDate);

    return (
        <div className="mt-2 small" data-testid={`failure-details-${video.id}`}>
            <span className="text-body-secondary">
                <FontAwesomeIcon icon={faCircleExclamation} className="me-1 text-danger" />
                Last failed{relative ? ` ${relative}` : ''}
                {video.lastFailureReason ? `: ${video.lastFailureReason}` : ''}
            </span>
            {video.lastFailureHtml && (
                <details className="mt-1">
                    <summary
                        className="text-primary"
                        style={{ cursor: 'pointer' }}
                        data-testid={`failure-html-toggle-${video.id}`}
                    >
                        View page HTML
                    </summary>
                    <pre
                        className="mt-2 mb-0 p-2 bg-body-secondary rounded small"
                        style={{ maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        data-testid={`failure-html-${video.id}`}
                    >
                        {video.lastFailureHtml}
                    </pre>
                </details>
            )}
        </div>
    );
};

export const Cleanup = () => {
    const apiClient = useApiClient();
    const [activeTab, setActiveTab] = useState<CleanupTab>("pending");
    const [pendingData, setPendingData] = useState<PendingDeletionViewModel | null>(null);
    const [deletedData, setDeletedData] = useState<DeletedVideosViewModel | null>(null);
    const [cleanupRuns, setCleanupRuns] = useState<CleanupRunViewModel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [resettingId, setResettingId] = useState<string | null>(null);
    const [resettingAll, setResettingAll] = useState<boolean>(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [restoringId, setRestoringId] = useState<string | null>(null);
    const [restoringAll, setRestoringAll] = useState<boolean>(false);
    const [selectedDeletedIds, setSelectedDeletedIds] = useState<string[]>([]);

    const loadData = useCallback(async () => {
        if (!apiClient) return;
        setLoading(true);
        setError(null);
        try {
            const [pending, deleted, runs] = await Promise.all([
                apiClient.pendingDeletion(),
                apiClient.deleted(),
                // Fetch one more than we display so the newest run can headline the card.
                apiClient.cleanupRuns(11).catch((e) => {
                    // The cleanup run history is informational; don't let it break the page.
                    console.error(e);
                    return [] as CleanupRunViewModel[];
                }),
            ]);
            setPendingData(pending);
            setDeletedData(deleted);
            setCleanupRuns(runs ?? []);
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [apiClient]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleResetOne = async (video: VideoViewModel) => {
        if (!await confirmAction(`Reset the retry count for "${video.title}"?`)) return;
        setResettingId(video.id ?? null);
        try {
            await apiClient!.resetRetries(video.id!);
            await loadData();
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        } finally {
            setResettingId(null);
        }
    };

    const handleResetAll = async () => {
        if (!await confirmAction("Reset the retry count for all videos pending deletion?")) return;
        setResettingAll(true);
        try {
            await apiClient!.resetAllRetries();
            await loadData();
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        } finally {
            setResettingAll(false);
        }
    };

    const handleDeleteOne = async (video: VideoViewModel) => {
        if (!await confirmAction(`Delete "${video.title}"? It will move to the Deleted tab, where you can restore it.`)) return;
        setDeletingId(video.id ?? null);
        setError(null);
        try {
            await apiClient!.videosDELETE(video.id!);
            await loadData();
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        } finally {
            setDeletingId(null);
        }
    };

    const handleRestore = async (video: VideoViewModel) => {
        setRestoringId(video.id ?? null);
        setError(null);
        try {
            await apiClient!.restoreVideo(video.id!);
            setSelectedDeletedIds((prev) => prev.filter((id) => id !== video.id));
            await loadData();
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        } finally {
            setRestoringId(null);
        }
    };

    const handleToggleDeletedSelected = (id: string) => {
        setSelectedDeletedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleToggleSelectAllDeleted = () => {
        setSelectedDeletedIds(allDeletedSelected ? [] : deletedVideos.filter((v) => v.id).map((v) => v.id!));
    };

    const handleRestoreSelected = async () => {
        const ids = selectedDeletedVideos.map((v) => v.id!);
        if (ids.length === 0) return;
        if (!await confirmAction(`Restore ${ids.length} video${ids.length === 1 ? '' : 's'}?`)) return;

        setRestoringAll(true);
        setError(null);
        try {
            await apiClient!.restoreVideos(ids);
            setSelectedDeletedIds([]);
            await loadData();
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        } finally {
            setRestoringAll(false);
        }
    };

    const pendingVideos = pendingData?.videos ?? [];
    const deletedVideos = deletedData?.videos ?? [];
    const selectedDeletedVideos = deletedVideos.filter((v) => v.id && selectedDeletedIds.includes(v.id));
    const allDeletedSelected = deletedVideos.length > 0 && selectedDeletedVideos.length === deletedVideos.length;
    const someDeletedSelected = selectedDeletedVideos.length > 0 && !allDeletedSelected;
    const pendingCount = pendingData?.totalCount ?? pendingVideos.length;
    const deletedCount = deletedData?.totalCount ?? deletedVideos.length;
    const retriesBeforeRemoval = pendingData?.retriesBeforeRemoval ?? 3;
    const resetBusy = resettingAll || resettingId !== null;
    const pendingBusy = resetBusy || deletingId !== null;
    const restoreBusy = restoringAll || restoringId !== null;
    const cleanupRun = cleanupRuns[0] ?? null;
    const previousRuns = cleanupRuns.slice(1);
    const lastRunRelative = formatRelativeTime(cleanupRun?.runDate);
    const lastRunAbsolute = cleanupRun?.runDate
        ? new Date(cleanupRun.runDate).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
        : null;
    const lastRunFailed = cleanupRun?.succeeded === false;

    const renderPending = () => (
        <>
            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-2 mb-3">
                <p className="text-body-secondary small mb-0">
                    Videos that are failing their periodic health checks. Once a video reaches {retriesBeforeRemoval + 1} failed
                    checks in a row it is deleted automatically.
                </p>
                {pendingVideos.length > 0 && (
                    <Button
                        variant="outline-secondary"
                        className="flex-shrink-0 align-self-start align-self-md-center"
                        onClick={handleResetAll}
                        disabled={resetBusy}
                        data-testid="restore-all"
                    >
                        {resettingAll ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Restoring...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                                Restore all
                            </>
                        )}
                    </Button>
                )}
            </div>

            {pendingVideos.length === 0 ? (
                <Alert variant="success" className="d-flex flex-column align-items-center text-center gap-2 py-5 border-0 rounded-4">
                    <FontAwesomeIcon icon={faCheckCircle} size="2x" className="opacity-75" />
                    <span className="fs-5 fw-semibold">Nothing pending deletion</span>
                    <span className="small text-body-secondary">
                        All of your videos passed their last health check.
                    </span>
                </Alert>
            ) : (
                <div className="d-flex flex-column gap-3" data-testid="pending-deletion-list">
                    {pendingVideos.map((video) => {
                        const retries = video.retries ?? 0;
                        const percent = Math.min(100, Math.round((retries / (retriesBeforeRemoval + 1)) * 100));
                        const isResetting = resettingId === video.id;
                        const isDeleting = deletingId === video.id;

                        return (
                            <Card key={video.id} className="shadow-sm">
                                <Card.Body className="d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                                    <div className="flex-grow-1 min-w-0">
                                        <h2 className="h6 mb-1 text-truncate">
                                            <a href={video.url!} target="_blank" rel="noreferrer noopener" title={video.title ?? ''}>
                                                {video.title}
                                            </a>
                                        </h2>
                                        <div className="d-flex align-items-center gap-2 flex-wrap text-body-secondary small">
                                            <Badge bg="primary-subtle" className="text-primary-emphasis">
                                                {video.categoryName ?? uncategorizedText}
                                            </Badge>
                                            <span className="text-truncate" title={video.url ?? ''}>{video.url}</span>
                                        </div>
                                        <FailureDetails video={video} />
                                    </div>

                                    <div style={{ minWidth: 180 }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1 small">
                                            <span className="text-body-secondary">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                                                Failed checks
                                            </span>
                                            <span className="fw-semibold">{retries} / {retriesBeforeRemoval + 1}</span>
                                        </div>
                                        <ProgressBar
                                            now={percent}
                                            variant={percent >= 100 ? "danger" : percent >= 50 ? "warning" : "info"}
                                            aria-label={`${retries} of ${retriesBeforeRemoval + 1} failed checks`}
                                        />
                                    </div>

                                    <div className="flex-shrink-0 d-flex gap-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleResetOne(video)}
                                            disabled={pendingBusy}
                                            title="Reset retry count"
                                        >
                                            {isResetting ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Resetting...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faSync} className="me-2" />
                                                    Reset
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteOne(video)}
                                            disabled={pendingBusy}
                                            title="Delete video"
                                        >
                                            {isDeleting ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Deleting...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                    Delete
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            )}

            {pendingVideos.length > 0 && (
                <p className="text-body-secondary small mt-4 mb-0 d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faTrash} />
                    Deleting moves a video to the Deleted tab where you can restore it. Resetting restarts its health-check
                    cycle so it isn't removed on the next run.
                </p>
            )}
        </>
    );

    const renderDeleted = () => (
        <>
            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-2 mb-3">
                <p className="text-body-secondary small mb-0">
                    Videos you deleted, or that were removed by the periodic health check. Restoring a video makes it visible
                    in your library again.
                </p>
                {deletedVideos.length > 0 && (
                    <div className="d-flex align-items-center gap-3 flex-shrink-0 align-self-start align-self-md-center">
                        <Form.Check
                            type="checkbox"
                            id="cleanup-select-all-deleted"
                            label="Select all"
                            className="mb-0 text-nowrap"
                            checked={allDeletedSelected}
                            onChange={handleToggleSelectAllDeleted}
                            disabled={restoreBusy}
                            ref={(el: HTMLInputElement | null) => {
                                if (el) el.indeterminate = someDeletedSelected;
                            }}                        />
                        <Button
                            variant="primary"
                            onClick={handleRestoreSelected}
                            disabled={restoreBusy || selectedDeletedVideos.length === 0}
                            data-testid="restore-selected"
                            title="Restore selected videos"
                        >
                            {restoringAll ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                    Restoring...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                                    Restore selected{selectedDeletedVideos.length > 0 ? ` (${selectedDeletedVideos.length})` : ''}
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>

            {deletedVideos.length === 0 ? (
                <Alert variant="success" className="d-flex flex-column align-items-center text-center gap-2 py-5 border-0 rounded-4">
                    <FontAwesomeIcon icon={faCheckCircle} size="2x" className="opacity-75" />
                    <span className="fs-5 fw-semibold">No deleted videos</span>
                    <span className="small text-body-secondary">
                        Videos you delete will appear here so you can restore them.
                    </span>
                </Alert>
            ) : (
                <div className="d-flex flex-column gap-3" data-testid="deleted-videos-list">
                    {deletedVideos.map((video) => {
                        const isRestoring = restoringId === video.id;
                        const deletedDate = formatDeletedDate(video.deletedDate);
                        const isSelected = !!video.id && selectedDeletedIds.includes(video.id);

                        return (
                            <Card key={video.id} className="shadow-sm">
                                <Card.Body className="d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                                    <Form.Check
                                        type="checkbox"
                                        id={`cleanup-deleted-${video.id}`}
                                        aria-label={`Select ${video.title}`}
                                        className="flex-shrink-0"
                                        checked={isSelected}
                                        onChange={() => video.id && handleToggleDeletedSelected(video.id)}
                                        disabled={restoreBusy}
                                    />

                                    <div className="flex-grow-1 min-w-0">
                                        <h2 className="h6 mb-1 text-truncate">
                                            <a href={video.url!} target="_blank" rel="noreferrer noopener" title={video.title ?? ''}>
                                                {video.title}
                                            </a>
                                        </h2>
                                        <div className="d-flex align-items-center gap-2 flex-wrap text-body-secondary small">
                                            <Badge bg="primary-subtle" className="text-primary-emphasis">
                                                {video.categoryName ?? uncategorizedText}
                                            </Badge>
                                            {video.tags?.map((tag) => (
                                                <Badge key={tag} bg="secondary-subtle" className="text-secondary-emphasis">
                                                    {tag}
                                                </Badge>
                                            ))}
                                            <span className="text-truncate" title={video.url ?? ''}>{video.url}</span>
                                        </div>
                                        <FailureDetails video={video} />
                                    </div>

                                    {deletedDate && (
                                        <div className="text-body-secondary small flex-shrink-0">
                                            <div className="fw-semibold">Deleted</div>
                                            <span title={video.deletedDate?.toString()}>{deletedDate}</span>
                                        </div>
                                    )}

                                    <div className="flex-shrink-0">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleRestore(video)}
                                            disabled={restoreBusy}
                                            title="Restore video"
                                        >
                                            {isRestoring ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Restoring...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                                                    Restore
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            )}
        </>
    );

    return (
        <AppLayout loading={loading}>
            <Container className="py-2">
                <div className="mb-3">
                    <h1 className="mb-1 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faBroom} className="text-secondary" />
                        Cleanup
                    </h1>
                    <p className="text-body-secondary mb-0">
                        Manage videos that are failing their periodic health checks or have been deleted.
                    </p>
                </div>

                <Card className="shadow-sm border-0 mb-3" data-testid="cleanup-runs-card">
                    <Card.Body className="p-0">
                        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 px-3 py-3">
                            <div className="d-flex align-items-center gap-3">
                                <FontAwesomeIcon
                                    icon={lastRunFailed ? faExclamationTriangle : faHistory}
                                    className={lastRunFailed ? "text-warning" : "text-secondary"}
                                    size="lg"
                                />
                                <div>
                                    <div className="fw-semibold">Last cleanup run</div>
                                    <div className="text-body-secondary small" title={lastRunAbsolute ?? undefined}>
                                        {!cleanupRun
                                            ? "No cleanup run recorded yet."
                                            : lastRunFailed
                                                ? `Failed ${lastRunRelative}`
                                                : `Ran ${lastRunRelative}`}
                                    </div>
                                </div>
                            </div>
                            {cleanupRun && (
                                <div className="d-flex align-items-center gap-4 text-body-secondary small">
                                    <div>
                                        <div data-testid="cleanup-run-checked" className="fw-semibold text-body fs-5">
                                            {cleanupRun.videosChecked ?? 0}
                                        </div>
                                        <div>videos checked</div>
                                    </div>
                                    <div>
                                        <div data-testid="cleanup-run-removed" className="fw-semibold text-body fs-5">
                                            {cleanupRun.videosRemoved ?? 0}
                                        </div>
                                        <div>videos removed</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {previousRuns.length > 0 && (
                            <>
                                <div className="d-flex align-items-center justify-content-between gap-2 border-top px-3 py-2">
                                    <span className="fw-semibold d-flex align-items-center gap-2">
                                        <FontAwesomeIcon icon={faHistory} className="text-secondary" />
                                        Previous runs
                                    </span>
                                    <Badge bg="secondary-subtle" className="text-secondary-emphasis rounded-pill">
                                        {previousRuns.length}
                                    </Badge>
                                </div>
                                <ListGroup variant="flush" data-testid="cleanup-runs-list">
                                    {previousRuns.map((run, index) => {
                                        const failed = run.succeeded === false;
                                        const relative = formatRelativeTime(run.runDate);
                                        const absolute = run.runDate
                                            ? new Date(run.runDate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                                            : undefined;

                                        return (
                                            <ListGroup.Item
                                                key={run.runDate ? run.runDate.toISOString() : index}
                                                className="d-flex align-items-center justify-content-between gap-3 py-2"
                                            >
                                                <span className="d-flex align-items-center gap-2 min-w-0">
                                                    <FontAwesomeIcon
                                                        icon={failed ? faExclamationTriangle : faCheckCircle}
                                                        className={failed ? "text-warning" : "text-success"}
                                                    />
                                                    <span className="text-truncate" title={absolute}>
                                                        {failed ? "Failed" : "Ran"} {relative ?? "just now"}
                                                    </span>
                                                </span>
                                                <span className="text-body-secondary small text-nowrap">
                                                    {`${run.videosChecked ?? 0} checked · ${run.videosRemoved ?? 0} removed`}
                                                </span>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
                        {error}
                    </Alert>
                )}

                <Nav
                    variant="tabs"
                    activeKey={activeTab}
                    onSelect={(key) => setActiveTab((key as CleanupTab) ?? "pending")}
                    className="mb-3"
                >
                    <Nav.Item>
                        <Nav.Link eventKey="pending" data-testid="cleanup-tab-pending" className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                            <span>Pending deletion</span>
                            <Badge
                                bg={pendingCount > 0 ? "danger-subtle" : "success-subtle"}
                                className={`${pendingCount > 0 ? "text-danger-emphasis" : "text-success-emphasis"} rounded-pill`}
                            >
                                {pendingCount}
                            </Badge>
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="deleted" data-testid="cleanup-tab-deleted" className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faTrashCan} />
                            <span>Deleted</span>
                            <Badge
                                bg={deletedCount > 0 ? "secondary-subtle" : "success-subtle"}
                                className={`${deletedCount > 0 ? "text-secondary-emphasis" : "text-success-emphasis"} rounded-pill`}
                            >
                                {deletedCount}
                            </Badge>
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {activeTab === "pending" ? renderPending() : renderDeleted()}
            </Container>
        </AppLayout>
    );
};
